import { createReference, MedplumClient, ProfileResource } from "@medplum/core";
import { Attachment, Bundle, BundleEntry, Communication, Provenance } from "@medplum/fhirtypes";

import chunk from "lodash/chunk";

import { CommunicationInbox } from "@/store/slices/inbox";
import { getResourcesFromBundle } from "@/utils/medplum";
import { showSystemErrorNotification } from "@/utils/notifications";
import { fetchAllPaginatedResourcesWithRevinclude } from "@/utils/pagination";
import { createAttachmentWithHash } from "@/utils/uploads";

import { COMMUNICATION_INBOX_CATEGORY } from "../constants/messages";
import {
  getCommunicationReadProvenance,
  getCommunicationThreadQuery,
  getCommunicationThreadsQuery,
} from "../utils/messages";

/** Threads per unread-messages search: a few hundred references would make the URL too long. */
const UNREAD_SEARCH_THREAD_CHUNK = 100;

/**
 * Messages per page of an unread-messages search. `_revinclude` puts every reader's receipt for
 * each message on the same page, so a page holds the messages plus up to one receipt per reader per
 * message. At 100 messages a page stays under Medplum's 1000-entry page limit while messages
 * average fewer than 9 receipts.
 */
const UNREAD_SEARCH_PAGE_SIZE = 100;

const COMMUNICATION_STATUS_MAX_RETRIES = 3;

/**
 * Loads an inbox thread by id.
 *
 * @param communicationId - the thread's id.
 * @param recipient - a reference string, such as `Patient/{id}`, that must be one of the thread's
 * recipients. Leave it out to skip that check.
 * @returns the thread, or `undefined` when no inbox thread matches.
 */
export async function fetchCommunication(
  medplum: MedplumClient,
  communicationId: CommunicationInbox["id"],
  recipient?: string,
): Promise<Communication | undefined> {
  return await medplum.searchOne(
    "Communication",
    {
      _id: communicationId,
      status: "in-progress,completed",
      category: COMMUNICATION_INBOX_CATEGORY,
      ...(recipient && { recipient }),
    },
    { cache: "no-cache" },
  );
}

export async function fetchLatestThread(
  medplum: MedplumClient,
  threadId: string,
): Promise<Communication> {
  return await medplum.readResource("Communication", threadId, { cache: "no-cache" });
}

export async function updateCommunicationStatus(
  medplum: MedplumClient,
  communication: Communication,
  status: Communication["status"],
  retries = 0,
) {
  let updatedCommunication: Communication | undefined;
  try {
    updatedCommunication = await medplum.patchResource("Communication", communication.id!, [
      {
        op: "test",
        path: "/meta/versionId",
        value: communication.meta?.versionId,
      },
      {
        op: "replace",
        path: "/status",
        value: status,
      },
    ]);
  } catch (error) {
    updatedCommunication = await medplum.readResource("Communication", communication.id!);
    if (!updatedCommunication) {
      showSystemErrorNotification();
      throw new Error(`Communication not found: ${communication.id}`);
    }

    if (retries >= COMMUNICATION_STATUS_MAX_RETRIES) {
      console.error(`Unable to update communication status: ${communication.id}`);
      return updatedCommunication;
    }

    if (updatedCommunication?.status !== status) {
      return updateCommunicationStatus(medplum, updatedCommunication, status, retries + 1);
    }
  }

  return updatedCommunication;
}

export async function setRelatedCommunicationsAsRead(
  medplum: MedplumClient,
  communication: Communication,
) {
  const profile = medplum.getProfile();
  if (!profile) return;
  const profileReference = createReference(profile);

  const unreadCommunications = await fetchUnreadCommunications(medplum, profile, communication);
  const entry: BundleEntry<Provenance>[] = [];
  const occurredDateTime = new Date().toISOString();
  for (const unreadCommunication of unreadCommunications) {
    entry.push({
      request: {
        method: "POST",
        url: "Provenance",
      },
      resource: getCommunicationReadProvenance(unreadCommunication, profileReference, {
        occurredDateTime,
      }),
    });
  }

  if (entry.length === 0) return;

  return await medplum.executeBatch({
    resourceType: "Bundle",
    type: "transaction",
    entry,
  });
}

export async function setCommunicationAsRead(medplum: MedplumClient, communication: Communication) {
  const profile = medplum.getProfile();
  if (!profile) return;

  const unreadCommunications = await fetchUnreadCommunications(medplum, profile, communication, {
    _id: communication.id,
    "part-of": communication.partOf?.[0]?.reference,
  });
  if (unreadCommunications.length === 0) return;

  const provenance = getCommunicationReadProvenance(communication, createReference(profile));
  return await medplum.createResource(provenance);
}

export async function uploadAttachments(
  medplum: MedplumClient,
  files: File[],
): Promise<Attachment[]> {
  const profile = medplum.getProfile();
  if (!profile) return [];

  const attachments: Attachment[] = await Promise.all(
    files.map(
      async (file) =>
        await createAttachmentWithHash(medplum, {
          data: file,
          contentType: file.type,
          filename: file.name,
        }),
    ),
  );

  return attachments;
}

export async function fetchUnreadCommunications(
  medplum: MedplumClient,
  profile: ProfileResource,
  communication: Communication,
  extra?: Record<string, string | number | boolean | undefined>,
) {
  const query = getCommunicationThreadQuery(communication, profile, extra);
  const response = await medplum.searchResources("Communication", query, { cache: "no-cache" });
  const bundle = response.bundle as Bundle<Communication | Provenance>;
  return readUnreadCommunications(
    getResourcesFromBundle("Communication", bundle) as Communication[],
    getResourcesFromBundle("Provenance", bundle) as Provenance[],
    profile,
  );
}

/**
 * Counts the user's unread messages in each thread, searching up to `UNREAD_SEARCH_THREAD_CHUNK`
 * threads at a time instead of once per thread. Every page of each search is read, and a page
 * that fails to load throws, so a count is never short.
 *
 * @param medplum - the Medplum client.
 * @param profile - the user whose unread messages are counted.
 * @param threads - the thread Communications.
 * @returns the unread count per thread reference (`Communication/<id>`); threads with none are absent.
 * @throws when a search page fails to load.
 */
export async function fetchUnreadCountsByThread(
  medplum: MedplumClient,
  profile: ProfileResource,
  threads: Communication[],
): Promise<Map<string, number>> {
  const threadReferences = threads.map((thread) => createReference(thread).reference);

  const unreadPerChunk = await Promise.all(
    chunk(threadReferences, UNREAD_SEARCH_THREAD_CHUNK).map(async (chunkReferences) => {
      // The search also returns messages already read, so every page is read before counting.
      const query = getCommunicationThreadsQuery(chunkReferences, profile, {
        _count: UNREAD_SEARCH_PAGE_SIZE,
      });
      const result = await fetchAllPaginatedResourcesWithRevinclude<Communication, Provenance>(
        medplum,
        "Communication",
        "Provenance",
        query,
        { throwOnPageError: true, cache: "no-cache" },
      );
      if (!result.success) throw result.error;
      return readUnreadCommunications(result.data.primary, result.data.included, profile);
    }),
  );

  return unreadPerChunk.flat().reduce((counts, message) => {
    const threadReference = message.partOf?.[0]?.reference;
    if (threadReference) counts.set(threadReference, (counts.get(threadReference) ?? 0) + 1);
    return counts;
  }, new Map<string, number>());
}

/**
 * Keeps the messages of a thread search that the user has no read receipt for.
 *
 * @param communications - the messages a thread search returned.
 * @param provenances - the read receipts revincluded with them.
 * @param profile - the user whose read receipts count.
 * @returns the unread messages.
 */
function readUnreadCommunications(
  communications: Communication[],
  provenances: Provenance[],
  profile: ProfileResource,
): Communication[] {
  const profileReference = createReference(profile);
  const provenanceByTarget = provenances.reduce(
    (acc, provenance) => {
      const { reference: targetReference } = provenance.target?.[0] ?? {};
      if (!targetReference) return acc;

      const { reference: agentReference } = provenance.agent?.[0]?.who ?? {};
      if (!agentReference || agentReference !== profileReference.reference) return acc;

      acc[targetReference] = provenance;
      return acc;
    },
    {} as Record<string, Provenance>,
  );

  return communications.filter(
    (communication) => !provenanceByTarget[createReference(communication).reference],
  );
}
